import {WTO_MEMBERS} from '../../../../libs/constant'

// 'WLD' is the pre-existing "World" party used elsewhere in this codebase
// (national trade-analysis pages default their partner selector to it) —
// reused here instead of inventing a synthetic value, since it's already
// a recognized code in dss_economies.
export const WORLD_PARTY = 'WLD'

export const isWtoMember = (iso: string): boolean =>
  iso === WORLD_PARTY || (WTO_MEMBERS as readonly string[]).includes(iso)

export type PartiesCountry = {
  value: string
  region_un?: string[]
  subregion_un?: string[]
  escap?: boolean
}

export type DerivedPartiesFields = {
  regionArray: string[]
  subregionArray: string[]
  crossregional: 'Yes' | 'No'
  partiesEscap: 'Yes' | 'No'
  partiesWto: 'yes' | 'no'
}

// Selecting WLD short-circuits the normal per-country derivation — it isn't
// a real country with region/subregion flags, so region/crossregional/
// parties_escap/parties_wto are fixed rather than computed from `countryList`.
export function deriveFieldsFromParties(
  selectedValues: string[],
  countryList: PartiesCountry[]
): DerivedPartiesFields {
  if (selectedValues.includes(WORLD_PARTY)) {
    return {
      regionArray: ['World'],
      subregionArray: [],
      crossregional: 'Yes',
      partiesEscap: 'Yes',
      partiesWto: 'yes',
    }
  }

  const selectedCountries = countryList.filter((c) => selectedValues.includes(c.value))

  const regionSet = new Set<string>()
  const subregionSet = new Set<string>()
  selectedCountries.forEach((c) => {
    c.region_un?.forEach((r) => regionSet.add(r))
    c.subregion_un?.forEach((r) => subregionSet.add(r))
  })

  const regionArray = Array.from(regionSet)
  const subregionArray = Array.from(subregionSet)
  const crossregional: 'Yes' | 'No' = regionArray.length > 1 ? 'Yes' : 'No'
  const partiesEscap: 'Yes' | 'No' = selectedCountries.some((c) => c.escap) ? 'Yes' : 'No'
  const partiesWto: 'yes' | 'no' =
    selectedValues.length > 0 && selectedValues.every(isWtoMember) ? 'yes' : 'no'

  return {regionArray, subregionArray, crossregional, partiesEscap, partiesWto}
}

export function buildAgreementFormEntries(
  agreementDetail: Record<string, any>,
  skipKeys: string[]
): Array<[string, string]> {
  const entries: Array<[string, string]> = []

  Object.entries(agreementDetail).forEach(([key, value]) => {
    if (skipKeys.includes(key)) return
    if (value === null || value === undefined) return

    let formattedValue = value

    if (key === 'date_signed' && value === '') {
      formattedValue = 'XXXX-XX-XX'
    }

    if ((key === 'notification' || key === 'type' || key === 'iso') && Array.isArray(value)) {
      formattedValue = value.join(';')
    }

    if (key === 'crossregional' || key === 'parties_escap') {
      formattedValue = String(value).toLowerCase()
    }

    if (key === 'composition') {
      formattedValue = String(value).trim()
    }

    entries.push([key, String(formattedValue)])
  })

  return entries
}

export function getErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.message || fallback
}

export type AgreementSource = {
  id: string // local id (uuid / timestamp)
  alias: string
  link: string
}

export function parseSources(raw?: string): AgreementSource[] {
  if (!raw) return []

  return raw
    .split(';')
    .map((segment, index) => {
      const trimmed = segment.trim()
      if (!trimmed) return null

      if (trimmed.includes('|')) {
        const [alias, link] = trimmed.split('|')
        return {
          id: `${Date.now()}_${index}`,
          alias: alias || link,
          link,
        }
      }

      // legacy case: a plain URL with no alias|link pipe
      return {
        id: `${Date.now()}_${index}`,
        alias: trimmed,
        link: trimmed,
      }
    })
    .filter((source): source is AgreementSource => source !== null)
}
